import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Typography, Card, CardContent, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, Paper,
  CircularProgress, Alert, IconButton, TextField, Button,
  Divider, useTheme, useMediaQuery,
} from '@mui/material';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import EmojiEventsIcon  from '@mui/icons-material/EmojiEvents';
import CheckCircleIcon  from '@mui/icons-material/CheckCircle';
import StarIcon         from '@mui/icons-material/Star';
import StarBorderIcon   from '@mui/icons-material/StarBorder';
import HourglassIcon    from '@mui/icons-material/HourglassEmpty';
import SaveIcon         from '@mui/icons-material/Save';
import {
  useGetDirectorEventsQuery,
  useGetAllEventFeedbacksQuery,
  useSaveDirectorFeedbackMutation,
} from '../../redux/services/director-api';
import { FEEDBACK_STAR_THRESHOLD } from '../../constants/director-constants';
import ScoreDisplay from '../../components/director/ScoreDisplay';
import { getScoreConfig, getSemaforoByPercent, showsColor, showsNumber } from '../../utils/director-score';

// ── Semáforo dot colors ───────────────────────────────────────────────────────
const DOT = {
  green:    '#10B981',
  yellow:   '#F59E0B',
  red:      '#EF4444',
  gray:     '#9CA3AF',
  ungraded: '#9CA3AF',
  unsynced: '#9CA3AF',
};

const LEGEND = [
  { status: 'green',  color: DOT.green,  label: 'Verde — 70% a 100%+' },
  { status: 'yellow', color: DOT.yellow, label: 'Amarillo — 40% a <70%' },
  { status: 'red',    color: DOT.red,    label: 'Rojo — <40%' },
];

const COMMENT_TYPES = [
  { value: 'event', label: 'El evento', bg: '#FFF8F0', color: '#B45309' },
  { value: 'judge', label: 'El juez',   bg: '#F0F5FF', color: '#1D4ED8' },
  { value: 'both',  label: 'Ambos',     bg: '#F0FDF4', color: '#166534' },
];

// ── Mini star rating ──────────────────────────────────────────────────────────
function MiniStars({ value, onChange }) {
  const [hovered, setHovered] = useState(0);
  return (
    <Box sx={{ display: 'flex', gap: 0 }}>
      {[1, 2, 3, 4, 5].map((n) => (
        <IconButton
          key={n}
          size="small"
          onMouseEnter={() => setHovered(n)}
          onMouseLeave={() => setHovered(0)}
          onClick={() => onChange(n)}
          sx={{ p: 0.15, color: (hovered || value) >= n ? '#F59E0B' : '#D1D5DB' }}
        >
          {(hovered || value) >= n
            ? <StarIcon sx={{ fontSize: 16 }} />
            : <StarBorderIcon sx={{ fontSize: 16 }} />}
        </IconButton>
      ))}
    </Box>
  );
}

// ── Metric card ───────────────────────────────────────────────────────────────
function MetricCard({ label, value, icon: Icon, color }) {
  return (
    <Card sx={{ flex: '1 1 140px', minWidth: 0 }}>
      <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2, py: '14px !important', px: 2 }}>
        <Box sx={{
          p: 1.25,
          borderRadius: 2,
          backgroundColor: `${color}20`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}>
          <Icon sx={{ color, fontSize: 24 }} />
        </Box>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 800, lineHeight: 1, fontSize: '1.5rem' }}>{value ?? '—'}</Typography>
          <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.72rem', fontWeight: 500 }}>{label}</Typography>
        </Box>
      </CardContent>
    </Card>
  );
}

// ── Panel de comentario para eventos rojos ────────────────────────────────────
function RedEventPanel({ fb, onCommentTypeChange, onCommentChange }) {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      <Typography variant="caption" sx={{ color: '#991B1B', fontWeight: 600 }}>
        Calificación baja — agrega tu valoración
      </Typography>
      <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
        {COMMENT_TYPES.map(({ value, label, bg, color }) => (
          <Box
            key={value}
            component="button"
            onClick={() => onCommentTypeChange(value)}
            sx={{
              border: `1px solid ${fb.commentType === value ? color : '#D1D5DB'}`,
              borderRadius: 1,
              px: 1.25,
              py: 0.4,
              fontSize: '0.78rem',
              fontWeight: fb.commentType === value ? 600 : 400,
              background: fb.commentType === value ? bg : '#fff',
              color: fb.commentType === value ? color : '#6B7280',
              cursor: 'pointer',
              transition: 'all 0.15s',
            }}
          >
            {label}
          </Box>
        ))}
      </Box>
      <TextField
        multiline
        minRows={2}
        maxRows={4}
        size="small"
        placeholder="Escribe tu observación…"
        value={fb.comment}
        onChange={(e) => onCommentChange(e.target.value)}
        sx={{ '& .MuiOutlinedInput-root': { fontSize: '0.875rem', backgroundColor: '#fff' } }}
      />
    </Box>
  );
}

// ── Dashboard ─────────────────────────────────────────────────────────────────
const COL_COUNT = 4;

export default function DirectorDashboard() {
  const navigate = useNavigate();
  const theme    = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const { data, isLoading, isError }          = useGetDirectorEventsQuery();
  const { data: allFeedbacks = [] }           = useGetAllEventFeedbacksQuery();
  const [saveFeedback, { isLoading: saving }] = useSaveDirectorFeedbackMutation();

  const [localFeedback, setLocalFeedback] = useState({});
  const [saveError, setSaveError]         = useState(null);
  const [saveSuccess, setSaveSuccess]     = useState(false);
  const [expandedFeedbackEventId, setExpandedFeedbackEventId] = useState(null);

  // Pre-populate local state from fetched feedbacks
  useEffect(() => {
    if (!allFeedbacks.length) return;
    const map = {};
    allFeedbacks.forEach(fb => {
      map[fb.event] = {
        stars:       fb.stars,
        comment:     fb.comment ?? '',
        commentType: fb.commentType,
      };
    });
    setLocalFeedback(map);
  }, [allFeedbacks]);

  const getFor = (eventId) => localFeedback[eventId] ?? { stars: 0, comment: '', commentType: null };

  const setFor = (eventId, field, value) =>
    setLocalFeedback(prev => ({
      ...prev,
      [eventId]: { ...getFor(eventId), ...prev[eventId], [field]: value },
    }));

  // Configuración de visualización del campamento (semáforo / puntuación / ambos)
  const { mode: scoreMode, format: scoreFormat } = getScoreConfig(data?.activeCamp);
  const scoreColumnLabel = showsNumber(scoreMode) ? 'Calificación' : 'Calificación en Colores';

  const getEventMaxPoints = (event) => Number(event?.maxPointEvents ?? event?.maxGrade ?? 0);

  const getEventSemaforo = (event) =>
    getSemaforoByPercent(event?.score, getEventMaxPoints(event), event?.semaforo);

  const metrics = useMemo(() => {
    if (!data?.events?.length) return null;
    const graded  = data.events.filter(e => e.semaforo?.status !== 'ungraded' && e.semaforo?.status !== 'unsynced');
    const pending = data.events.length - graded.length;
    const fbSet   = new Set(allFeedbacks.map(f => f.event?.toString?.() ?? f.event));
    const withFb  = data.events.filter(e => fbSet.has(e._id)).length;
    return { graded: graded.length, total: data.events.length, pending, withFeedback: withFb };
  }, [data, allFeedbacks]);

  const handleSave = async () => {
    setSaveError(null);
    setSaveSuccess(false);
    const entries = Object.entries(localFeedback).filter(([, v]) => v.stars > 0);
    if (!entries.length) return;

    const invalid = entries.find(([, v]) => v.stars <= FEEDBACK_STAR_THRESHOLD && !v.comment?.trim());
    if (invalid) {
      setSaveError(`Debes agregar comentario cuando la valoración es menor a ${FEEDBACK_STAR_THRESHOLD} estrellas.`);
      return;
    }

    try {
      for (const [eventId, value] of entries) {
        await saveFeedback({
          eventId,
          stars:       value.stars,
          comment:     value.comment,
          commentType: value.commentType,
        }).unwrap();
      }
      setSaveSuccess(true);
    } catch (err) {
      setSaveError(err?.data?.message ?? 'Error al guardar.');
    }
  };

  const autoSaveStars = async (eventId, stars, currentFb) => {
    if (stars <= FEEDBACK_STAR_THRESHOLD && !currentFb?.comment?.trim()) {
      setSaveError(`Agrega comentario para guardar valoraciones menores a ${FEEDBACK_STAR_THRESHOLD} estrellas.`);
      return;
    }

    try {
      setSaveError(null);
      await saveFeedback({
        eventId,
        stars,
        comment: currentFb?.comment ?? '',
        commentType: currentFb?.commentType ?? null,
      }).unwrap();
    } catch (err) {
      setSaveError(err?.data?.message ?? 'Error al guardar automáticamente.');
    }
  };

  const handleSaveOne = async (eventId) => {
    setSaveError(null);
    setSaveSuccess(false);

    const value = getFor(eventId);
    if (!value?.stars) {
      setSaveError('Selecciona una valoración en estrellas antes de guardar.');
      return;
    }

    if (value.stars <= FEEDBACK_STAR_THRESHOLD && !value.comment?.trim()) {
      setSaveError(`Debes agregar comentario cuando la valoración es menor a ${FEEDBACK_STAR_THRESHOLD} estrellas.`);
      return;
    }

    try {
      await saveFeedback({
        eventId,
        stars: value.stars,
        comment: value.comment,
        commentType: value.commentType,
      }).unwrap();
      setSaveSuccess(true);
    } catch (err) {
      setSaveError(err?.data?.message ?? 'Error al guardar.');
    }
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', pt: 8 }}>
        <CircularProgress sx={{ color: '#284B32' }} />
      </Box>
    );
  }

  if (isError) {
    return <Alert severity="error" sx={{ m: 2 }}>No se pudo cargar la información del dashboard.</Alert>;
  }

  if (!data?.activeCamp) {
    return (
      <Box sx={{ textAlign: 'center', py: 8, px: 2 }}>
        <EmojiEventsIcon sx={{ fontSize: 56, color: '#284B32', opacity: 0.3, mb: 2 }} />
        <Typography variant="h6" sx={{ color: '#284B32', fontWeight: 600 }}>
          No hay campamento activo en este momento
        </Typography>
        <Typography color="text.secondary" sx={{ mt: 1, maxWidth: 400, mx: 'auto' }}>
          Tu club será inscrito al próximo campamento automáticamente.
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      {/* ── Encabezado ── */}
      <Box sx={{ mb: 3, pl: 2, borderLeft: '4px solid #284B32' }}>
        <Typography variant="h5" sx={{ fontWeight: 700, color: '#284B32', lineHeight: 1.2 }}>Dashboard</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>{data.activeCamp.name}</Typography>
      </Box>

      {/* ── Banner de publicación ── */}
      {data.activeCamp.gradesPublished ? (
        <Alert
          severity="success"
          sx={{ mb: 3, backgroundColor: '#F0FDF4', color: '#166534', '& .MuiAlert-icon': { color: '#16a34a' } }}
        >
          Calificaciones publicadas el{' '}
          {new Date(data.activeCamp.gradesPublishedAt).toLocaleString('es', { dateStyle: 'long', timeStyle: 'short' })}
        </Alert>
      ) : (
        <Alert
          severity="warning"
          sx={{ mb: 3, backgroundColor: '#FFFBEB', color: '#92400e', '& .MuiAlert-icon': { color: '#D97706' } }}
        >
          Las calificaciones aún no han sido publicadas por el coordinador. Los resultados estarán disponibles próximamente.
        </Alert>
      )}

      {/* ── Métricas ── */}
      {metrics && (
        <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
          <MetricCard label="Evaluados"       value={`${metrics.graded}/${metrics.total}`} icon={CheckCircleIcon} color="#0F6E56" />
          <MetricCard label="Tu feedback"     value={metrics.withFeedback}                icon={StarIcon}        color="#F59E0B" />
          <MetricCard label="Pendientes"      value={metrics.pending}                     icon={HourglassIcon}   color="#6B7280" />
        </Box>
      )}

      {/* ── Leyenda de semáforo ── */}
      {showsColor(scoreMode) && (
        <Box sx={{ display: 'flex', gap: 2.5, flexWrap: 'wrap', mb: 2 }}>
          {LEGEND.map(({ status, color, label }) => (
            <Box key={status} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Box sx={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: color }} />
              <Typography variant="caption" color="text.secondary">{label}</Typography>
            </Box>
          ))}
        </Box>
      )}

      {/* ── Vista mobile: cards ── */}
      {isMobile && data.events.length > 0 && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mb: 2 }}>
          {data.events.map((event, idx) => {
            const canInteract   = event.semaforo?.status !== 'ungraded' && event.semaforo?.status !== 'unsynced';
            const eventSemaforo = getEventSemaforo(event);
            const fb            = getFor(event._id);
            const hasLowStars   = fb.stars > 0 && fb.stars < FEEDBACK_STAR_THRESHOLD;
            const hasHighStars  = fb.stars >= FEEDBACK_STAR_THRESHOLD;
            const showPanel     = canInteract && (hasLowStars || (expandedFeedbackEventId === event._id && hasHighStars));
            const disableRowSave = saving || !fb.stars;

            return (
              <Card key={event._id} variant="outlined" sx={{ opacity: canInteract ? 1 : 0.6 }}>
                <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1.5, mb: 1 }}>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600, lineHeight: 1.3 }}>{event.name}</Typography>
                      {event.event_type?.name && (
                        <Typography variant="caption" color="text.secondary">{event.event_type.name}</Typography>
                      )}
                    </Box>
                    <ScoreDisplay
                      semaforo={eventSemaforo}
                      score={event.score}
                      maxPoints={getEventMaxPoints(event)}
                      mode={scoreMode}
                      format={scoreFormat}
                      compact
                    />
                  </Box>

                  {canInteract && (
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 0.5 }}>
                      <MiniStars
                        value={fb.stars}
                        onChange={(n) => {
                          setFor(event._id, 'stars', n);
                          if (n <= FEEDBACK_STAR_THRESHOLD) {
                            setExpandedFeedbackEventId(event._id);
                          } else {
                            setExpandedFeedbackEventId(prev => (prev === event._id ? null : prev));
                          }
                          autoSaveStars(event._id, n, fb);
                        }}
                      />
                      <Button
                        size="small"
                        variant="text"
                        endIcon={<ChevronRightIcon fontSize="small" />}
                        onClick={() => navigate(`/director/evento/${event._id}`)}
                        sx={{ textTransform: 'none', fontWeight: 600, color: '#284B32', minWidth: 0 }}
                      >
                        Detalle
                      </Button>
                    </Box>
                  )}

                  {showPanel && (
                    <>
                      <Divider sx={{ my: 1 }} />
                      <RedEventPanel
                        fb={fb}
                        onCommentTypeChange={(v) => setFor(event._id, 'commentType', v)}
                        onCommentChange={(v) => setFor(event._id, 'comment', v)}
                      />
                      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
                        <Button
                          size="small"
                          variant="contained"
                          startIcon={<SaveIcon />}
                          onClick={() => handleSaveOne(event._id)}
                          disabled={disableRowSave}
                          sx={{ backgroundColor: '#0F6E56', '&:hover': { backgroundColor: '#0a5a45' }, textTransform: 'none', fontWeight: 600 }}
                        >
                          Guardar nota
                        </Button>
                      </Box>
                    </>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </Box>
      )}

      {/* ── Tabla de eventos (desktop) ── */}
      <TableContainer component={Paper} variant="outlined" sx={{ overflowX: 'auto', display: isMobile ? 'none' : undefined }}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ backgroundColor: '#284B32' }}>
              {['Evento', scoreColumnLabel, 'Tu valoración', ''].map((h, i) => (
                <TableCell key={i} sx={{ color: '#fff', fontWeight: 600, fontSize: '0.75rem', py: 1.25 }}>
                  {h}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {data.events.length === 0 && (
              <TableRow>
                <TableCell colSpan={COL_COUNT} sx={{ textAlign: 'center', py: 4, color: 'text.secondary' }}>
                  Sin eventos en este campamento.
                </TableCell>
              </TableRow>
            )}

            {data.events.map((event, idx) => {
              const canInteract  = event.semaforo?.status !== 'ungraded' && event.semaforo?.status !== 'unsynced';
              const eventSemaforo = getEventSemaforo(event);
              const fb           = getFor(event._id);
              const hasLowStars  = fb.stars > 0 && fb.stars < FEEDBACK_STAR_THRESHOLD;
              const hasHighStars = fb.stars >= FEEDBACK_STAR_THRESHOLD;
              const showPanel    = canInteract && (hasLowStars || (expandedFeedbackEventId === event._id && hasHighStars));
              const disableRowSave =
                saving
                || !fb.stars;
              const rowBg        = idx % 2 === 0 ? 'transparent' : 'rgba(0,0,0,0.02)';

              return (
                <React.Fragment key={event._id}>
                  <TableRow
                    hover={canInteract}
                    sx={{
                      backgroundColor: rowBg,
                      opacity: canInteract ? 1 : 0.55,
                      '& td': showPanel ? { borderBottom: 'none' } : {},
                    }}
                  >
                    {/* Evento */}
                    <TableCell
                      sx={{ cursor: canInteract ? 'pointer' : 'default', py: 1.25 }}
                      onClick={canInteract ? () => navigate(`/director/evento/${event._id}`) : undefined}
                    >
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>{event.name}</Typography>
                      {event.event_type?.name && (
                        <Typography variant="caption" color="text.secondary">{event.event_type.name}</Typography>
                      )}
                    </TableCell>

                    {/* Calificación */}
                    <TableCell sx={{ py: 1.25, minWidth: 100 }}>
                      <ScoreDisplay
                        semaforo={eventSemaforo}
                        score={event.score}
                        maxPoints={getEventMaxPoints(event)}
                        mode={scoreMode}
                        format={scoreFormat}
                      />
                    </TableCell>

                    {/* Estrellas inline */}
                    <TableCell sx={{ py: 1.25 }}>
                      {canInteract
                        ? (
                          <MiniStars
                            value={fb.stars}
                            onChange={(n) => {
                                setFor(event._id, 'stars', n);
                                if (n <= FEEDBACK_STAR_THRESHOLD) {
                                  setExpandedFeedbackEventId(event._id);
                                } else {
                                  setExpandedFeedbackEventId(prev => (prev === event._id ? null : prev));
                                }
                                autoSaveStars(event._id, n, fb);
                            }}
                          />
                        )
                        : <Typography variant="caption" color="text.disabled">—</Typography>}
                    </TableCell>

                    {/* Acciones */}
                    <TableCell sx={{ textAlign: 'right', pr: 0.5, py: 1.25 }}>
                      {canInteract && (
                        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', alignItems: 'center' }}>
                          {hasHighStars && (
                            <Button
                              size="small"
                              variant="outlined"
                              onClick={() => setExpandedFeedbackEventId(prev => (prev === event._id ? null : event._id))}
                              sx={{ textTransform: 'none', fontWeight: 600 }}
                            >
                              Agregar nota
                            </Button>
                          )}
                          {hasLowStars && (
                            <Typography variant="caption" sx={{ color: '#991B1B', fontWeight: 600 }}>
                              Feedback requerido
                            </Typography>
                          )}
                          <Button
                            size="small"
                            variant="text"
                            endIcon={<ChevronRightIcon fontSize="small" />}
                            onClick={() => navigate(`/director/evento/${event._id}`)}
                            sx={{ textTransform: 'none', fontWeight: 600, color: '#284B32' }}
                          >
                            Mas detalle
                          </Button>
                        </Box>
                      )}
                    </TableCell>
                  </TableRow>

                  {/* Panel expandido para feedback global */}
                  {showPanel && (
                    <TableRow sx={{ backgroundColor: '#FFF5F5' }}>
                      <TableCell colSpan={COL_COUNT} sx={{ py: 1.5, px: 2.5 }}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
                          <RedEventPanel
                            fb={fb}
                            onCommentTypeChange={(v) => setFor(event._id, 'commentType', v)}
                            onCommentChange={(v) => setFor(event._id, 'comment', v)}
                          />
                          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                            <Button
                              size="small"
                              variant="contained"
                              startIcon={<SaveIcon />}
                              onClick={() => handleSaveOne(event._id)}
                              disabled={disableRowSave}
                              sx={{
                                backgroundColor: '#0F6E56',
                                '&:hover': { backgroundColor: '#0a5a45' },
                                textTransform: 'none',
                                fontWeight: 600,
                              }}
                            >
                              Guardar nota
                            </Button>
                          </Box>
                        </Box>
                      </TableCell>
                    </TableRow>
                  )}
                </React.Fragment>
              );
            })}

            {/* Leyenda eliminada de aquí, ahora está arriba */}
          </TableBody>
        </Table>
      </TableContainer>

      {/* ── Estado y guardar ── */}
      {saveError   && <Alert severity="error"   sx={{ mt: 2 }}>{saveError}</Alert>}
      {saveSuccess && <Alert severity="success" sx={{ mt: 2 }}>Valoraciones guardadas.</Alert>}

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
        <Button
          variant="contained"
          startIcon={<SaveIcon />}
          onClick={handleSave}
          disabled={saving || !Object.values(localFeedback).some(v => v.stars > 0)}
          sx={{
            backgroundColor: '#0F6E56',
            '&:hover': { backgroundColor: '#0a5a45' },
            textTransform: 'none',
            fontWeight: 600,
          }}
        >
          {saving ? 'Guardando…' : 'Guardar valoraciones'}
        </Button>
      </Box>
    </Box>
  );
}
